/**
 * Age affirmation — ONE BIT, taken with the other consents.
 *
 * WHY A TICK AND NOT A DATE: the decision is "18 or over, yes/no". A full date of
 * birth is more than that decision needs, so it is not collected — there is no
 * date field, no derived age, and therefore nothing about age to hold for anyone,
 * whether they pass or are refused. A refusal records only that a refusal
 * happened (timestamp, programme, threshold in force, outcome, coarse session
 * ref) via `record_age_gate_block`, which takes no age argument by design. An
 * affirmation records only "granted" plus the threshold that was in force.
 *
 * WHERE IT SITS: on the consent step (S3), alongside biometric and terms. Capture
 * is allowed to happen before consent — a selfie may be held — because the thing
 * being gated is PROCESSING. Generation is what must not begin without all three
 * affirmations, and it is enforced server-side in `generate-avatar`, not by
 * routing. The repeat lane (S2b) carries the same tick, because its Continue
 * goes straight to generating.
 *
 * FAILS CLOSED: the tick is only "passed" once the server has recorded it. If the
 * write fails the affirmation is not held, and generation will refuse.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { readAgeGateMark, saveAgeGateMark, readGuestToken } from "@/lib/consumer-session";
import {
  getAgeGateConfig,
  getAgeAffirmationState,
  recordAgeAffirmation,
  recordAgeGateBlock,
} from "@/services/age-gate";
import { useProgrammeFigures, resolveFigures } from "@/lib/programme-figures";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S1B_SECTION, S1B_DEFAULTS } from "@/content/screens/s1b";
import {
  Display,
  FunnelContainer,
  FunnelPage,
  GhostButton,
  Lede,
} from "@/components/consumer/chrome";
import { cn } from "@/lib/utils";

export type AgeAffirmationStatus = "loading" | "required" | "passed" | "blocked";

/** Substitutes the configured threshold into counsel-owned copy. */
function withThreshold(copy: string, threshold: number | null) {
  return copy.replace(/\{\{\s*threshold\s*\}\}/g, String(threshold ?? 18));
}

export function useAgeAffirmation(programSlug: string, programId?: string | null) {
  /**
   * The local mark is ADVISORY, never authority. It is per-browser, so a person
   * who affirmed in an earlier session — or as a guest before signing in — was
   * shown the tick already made while the CURRENT scope (new guest session, or
   * the user record) held no affirmation. Generation then refused. The server is
   * asked on every mount; only a refusal is trusted from local state, because a
   * refusal must stay terminal even offline.
   */
  const [status, setStatus] = useState<AgeAffirmationStatus>(() =>
    readAgeGateMark(programSlug) === "blocked" ? "blocked" : "loading",
  );
  const [threshold, setThreshold] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await getAgeGateConfig();
      if (cancelled) return;
      setThreshold(cfg.thresholdYears);
      if (!cfg.enabled) {
        // Gate switched off by counsel: pass-through, nothing recorded.
        saveAgeGateMark(programSlug, "passed");
        setStatus("passed");
        return;
      }
      const server = await getAgeAffirmationState(readGuestToken(programSlug));
      if (cancelled) return;
      if (server?.blocked) {
        saveAgeGateMark(programSlug, "blocked");
        setStatus("blocked");
        return;
      }
      if (server?.affirmed) {
        saveAgeGateMark(programSlug, "passed");
        setStatus("passed");
        return;
      }
      // Fails CLOSED: no server-held affirmation (or an unreadable answer)
      // leaves the tick to be made again in THIS scope.
      setStatus((prev) => (prev === "blocked" ? prev : "required"));
    })();
    return () => { cancelled = true; };
  }, [programSlug]);

  /**
   * The affirmation is only held once the SERVER holds it. A failed write leaves
   * the tick unmade, so the generation gate stays shut.
   */
  const affirm = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const { error: rpcErr } = await recordAgeAffirmation(readGuestToken(programSlug));
      if (rpcErr) throw rpcErr;
      saveAgeGateMark(programSlug, "passed");
      setStatus("passed");
    } catch (e: any) {
      setError("We couldn't record that just now — please try again.");
    } finally {
      setBusy(false);
    }
  }, [programSlug]);

  /** Terminal. Records the refusal; a failed audit write never becomes a pass. */
  const decline = useCallback(async () => {
    saveAgeGateMark(programSlug, "blocked");
    setStatus("blocked");
    try {
      await recordAgeGateBlock(readGuestToken(programSlug), programId ?? null);
    } catch {
      /* audit failure must not soften the refusal */
    }
  }, [programSlug, programId]);

  return { status, threshold, error, busy, affirm, decline };
}

/**
 * The affirmation block. Rendered alongside the other things a person affirms
 * before processing begins.
 */
export function AgeAffirmationCard({
  affirmed,
  threshold,
  busy,
  error,
  onAffirm,
  onDecline,
  hideHint = false,
}: {
  affirmed: boolean;
  threshold: number | null;
  busy?: boolean;
  error?: string | null;
  onAffirm: () => void;
  onDecline: () => void;
  /** Label only — used where the screen carries no explanatory copy (S2b). */
  hideHint?: boolean;
}) {
  const { t } = useSiteContent(S1B_SECTION, S1B_DEFAULTS);
  return (
    <div className="mb-3" data-testid="age-affirmation">
      <label
        className={cn(
          "flex cursor-pointer items-start gap-3.5 rounded-[18px] border bg-white p-[18px] transition-colors",
          affirmed ? "border-grape" : "border-foreground/[0.16] hover:border-foreground/30",
        )}
      >
        <input
          type="checkbox"
          /* Never pre-ticked and never defaulted true: the affirmation only
             exists once the person makes it. */
          checked={affirmed}
          disabled={busy}
          onChange={(e) => { if (e.target.checked) onAffirm(); }}
          className="mt-0.5 h-[20px] w-[20px] flex-none accent-[#8834B2]"
          data-testid="age-affirmation-input"
        />
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="text-[16px] font-semibold leading-[1.35]">
            {withThreshold(t("s1b.affirm.label"), threshold)}
          </span>
          {hideHint ? null : (
            <span className="text-[14px] leading-[1.5] text-foreground/65">
              {t("s1b.affirm.hint")}
            </span>
          )}
        </span>
      </label>
      {error ? (
        <p className="mt-2 text-[14px] font-medium text-[#C41112]" data-testid="age-affirmation-error">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onDecline}
        className="mt-1.5 min-h-11 px-1 text-[14px] font-semibold text-foreground/60 underline hover:text-foreground"
        data-testid="age-affirmation-decline"
      >
        {withThreshold(t("s1b.affirm.decline"), threshold)}
      </button>
    </div>
  );
}

/**
 * The refusal. Terminal, with exactly ONE affordance: home. No retry, no
 * edit-and-resubmit, no parent/guardian route, no support link, and no path back
 * into the funnel — but not a dead end either.
 */
export function AgeRefusal() {
  const { t } = useSiteContent(S1B_SECTION, S1B_DEFAULTS);
  /* {minAge} comes from the CONFIGURED gate, never typed — the refusal must state
     the same threshold the gate applied. */
  const figures = useProgrammeFigures();
  return (
    <FunnelPage testId="s1b-age-gate-refused" pb="pb-[40px]">
      <FunnelContainer size="form">
        <div className="flex min-h-[70dvh] flex-col justify-center">
          <Display className="mb-4 max-w-[24ch]">{resolveFigures(t("s1b.refusal.line1"), figures) ?? ""}</Display>
          <Lede className="mb-8 max-w-[34ch]">{t("s1b.refusal.line2")}</Lede>
          <Link to="/" className="block max-w-[280px]" data-testid="age-gate-refused-home">
            <GhostButton className="w-full">{t("s1b.refusal.home")}</GhostButton>
          </Link>
        </div>
      </FunnelContainer>
    </FunnelPage>
  );
}
